import React, { useState, useEffect, useRef } from 'react';
import { connectToEventStream, connectToGameStream } from '../../redux/actions/gameActions';
import { Chessboard } from 'react-chessboard';
import { Chess } from 'chess.js';
import ChesssLogo from "../../images/chessslogo.png";
import Controls from "../../components/ArchivedChess/Controls";
import { connect } from 'react-redux';
import ActionConfirmModal from "../../components/modals/ActionConfirmModal";
import './PlayChess.css'; // Make sure to create this CSS file
import { BOARD_THEMES, DEFAULT_MOVE_HIGHLIGHT } from '../../components/WorkingChessLogic/BoardStyle';

// Import GG icons for checkmate, check and draw states
import winnerIcon from '../../images/GGIcons/Winner.png';
import loserIcon from '../../images/GGIcons/Loser.png';
import checkIcon from '../../images/GGIcons/Check.png';
import drawIcon from '../../images/GGIcons/Draw.png';

import { offerDraw, playMove, resignGame, startGameWithAI, challengeAPlayer } from '../../util/api/lichess';
import LichessLogo from "../../images/lichessgray.png";
import constants from '../../util/constants'; // Import constants
import { usePremium } from '../../util/PremiumFilter';


// Get color variants from constants
const COLOR_VARIANTS = constants.CHESSBOARD.COLOR_VARIANTS;

// Define the move highlight color
const MOVE_HIGHLIGHT_COLOR = { boxShadow: 'inset 0 0 0 4px rgba(88, 176, 71, 1)' }; // Green highlight for last move

// Define selected square highlight color
const SELECTED_SQUARE_COLOR = { boxShadow: 'inset 0 0 0 4px #FFD700' }; // Gold highlight for selected piece

// Define starting square border when dragging
const DRAGGING_SOURCE_SQUARE_STYLE = { border: '5px solid #58b047', borderRadius: '5px' };

// Material values for pieces
const pieceValues = {
  p: 1, n: 3, b: 3, r: 5, q: 9, k: 0, // Black pieces
  P: 1, N: 3, B: 3, R: 5, Q: 9, K: 0  // White pieces
};

// Function to calculate material advantage
const getMaterialAdvantage = (chess) => {
  const fen = chess.fen();
  const board = fen.split(" ")[0]; // Extract board position from FEN
  let whiteMaterial = 0, blackMaterial = 0;

  for (let char of board) {
    if (pieceValues[char]) {
      if (char === char.toUpperCase()) whiteMaterial += pieceValues[char]; // White pieces
      else blackMaterial += pieceValues[char]; // Black pieces
    }
  }

  return whiteMaterial - blackMaterial; // Positive = White is ahead, Negative = Black is ahead
};

// Function to calculate piece differential
const getPieceDifferential = (chess) => {
  const fen = chess.fen();
  const board = fen.split(" ")[0]; // Extract board position from FEN

  // Count of each piece type
  const pieceCounts = {
    p: 0, n: 0, b: 0, r: 0, q: 0, // Black pieces
    P: 0, N: 0, B: 0, R: 0, Q: 0  // White pieces
  };

  // Count pieces on the board
  for (let char of board) {
    if (pieceCounts[char] !== undefined) {
      pieceCounts[char]++;
    }
  }

  // Calculate differential for each piece type
  const differential = {
    pawn: pieceCounts['P'] - pieceCounts['p'],
    knight: pieceCounts['N'] - pieceCounts['n'],
    bishop: pieceCounts['B'] - pieceCounts['b'],
    rook: pieceCounts['R'] - pieceCounts['r'],
    queen: pieceCounts['Q'] - pieceCounts['q']
  };

  return differential;
};

const boardTheme = localStorage.getItem('boardTheme') || 'blue';
const currentTheme = BOARD_THEMES[boardTheme] || BOARD_THEMES.blue;

// Get custom colors from localStorage if the theme is 'custom'
const customColors = (() => {
  if (boardTheme === 'custom') {
    const savedCustomColors = localStorage.getItem('customColors');
    return savedCustomColors ? JSON.parse(savedCustomColors) : {
      dark: '#3066be',
      light: '#eeeed2'
    };
  }
  return null;
})();

const showCoordinates = localStorage.getItem('showCoordinates') === 'true';

// Get piece icons based on the differential
const renderPieceIcons = (differential, forWhite = true) => {
  // We'll display pieces that the side has in advantage
  const pieces = [];
  const pieceTypes = ['pawn', 'knight', 'bishop', 'rook', 'queen'];

  pieceTypes.forEach(type => {
    const diff = forWhite ? differential[type] : -differential[type];
    if (diff > 0) {
      // Add icons for each piece advantage
      for (let i = 0; i < diff; i++) {
        pieces.push(
          <i
            key={`${type}-${i}`}
            className={`fa-solid fa-chess-${type}`}
            style={{ marginRight: '3px', color: forWhite ? 'white' : 'white' }}
          ></i>
        );
      }
    }
  });

  return pieces;
};

const ChessGame = (props) => {
  const [game, setGame] = useState(new Chess());
  const [gameId, setGameId] = useState(null);
  const [fenHistory, setFenHistory] = useState([game.fen()]);
  const [currentMoveIndex, setCurrentMoveIndex] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [sanHistory, setSanHistory] = useState([]);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  // Add activeColor state for color selection
  const [activeColor, setActiveColor] = useState(COLOR_VARIANTS.YELLOW);

  const [materialAdvantage, setMaterialAdvantage] = useState(0); // Track material advantage
  const [pieceDifferential, setPieceDifferential] = useState({
    pawn: 0, knight: 0, bishop: 0, rook: 0, queen: 0
  });

  const [isOpponentHidden, setIsOpponentHidden] = useState(
    localStorage.getItem('isOpponentHidden') === 'true'
  ); // Track if opponent is hidden

  const [moveSquares, setMoveSquares] = useState({});
  const [lastMoveSquares, setLastMoveSquares] = useState(null);
  const [orientation, setOrientation] = useState('white');

  // Add state for tracking kings and game end state
  const [kingPositions, setKingPositions] = useState({ white: null, black: null });
  const [gameState, setGameState] = useState({
    isCheckmate: false,
    isCheck: false,
    isDraw: false,
    winner: null
  });

  // Save isOpponentHidden to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('isOpponentHidden', isOpponentHidden);
  }, [isOpponentHidden]);

  useEffect(() => {
    if (!gameId) {
      const currentGameId = props?.game?.games?.[0]?.gameId;
      setGameId(currentGameId);
    }
  }, [props.game]);

  useEffect(() => {

  }, [gameId])

  const toggleBoardOrientation = () => {
    setOrientation((prevOrientation) => (prevOrientation === 'white' ? 'black' : 'white'));
  };

  // Calculate the maximum board size based on the window height
  const calculateMaxBoardSize = () => {
    const minHeight = 400; // Minimum height for smaller screens
    // Reduce the amount of space reserved for other elements from 200px to 150px
    // This gives more leeway for the board size while still ensuring it fits
    const maxBoardSize = windowHeight <= minHeight ? 600 : windowHeight - 75;
    return maxBoardSize;
  };

  // Load board size from localStorage or calculate max size
  const initialBoardSize = localStorage.getItem('boardSize')
    ? parseInt(localStorage.getItem('boardSize'), 10)
    : calculateMaxBoardSize();

  const [boardSize, setBoardSize] = useState(initialBoardSize);

  // Save board size to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('boardSize', boardSize);
  }, [boardSize]);

  const [highlightSquares, setHighlightSquares] = useState({});

  // Add a state to track whether to show legal moves (initialize from localStorage)
  const [showLegalMoves, setShowLegalMoves] = useState(
    localStorage.getItem('showLegalMoves') !== 'false' // Default to true if not explicitly set to false
  );

  // Handle keyboard shortcuts for color selection
  const handleKeyDown = (event) => {
    let newColor = COLOR_VARIANTS.YELLOW;
    switch (event.key) {
      case "1":
        newColor = COLOR_VARIANTS.BLUE;
        break;
      case "2":
        newColor = COLOR_VARIANTS.GREEN;
        break;
      case "3":
        newColor = COLOR_VARIANTS.RED;
        break;
      default:
        // Keep current color for other keys
        return;
    }
    setActiveColor(newColor);
  };

  const handleKeyUp = (event) => {
    if (["1", "2", "3"].includes(event.key)) {
      setActiveColor(COLOR_VARIANTS.YELLOW); // Revert to default color when key is released
    }
  };

  // Add event listeners for keyboard shortcuts
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  const handleSquareRightClick = (square) => {
    // Check if this square is already highlighted
    if (highlightSquares[square] && highlightSquares[square].backgroundColor === activeColor) {
      // If already highlighted with current color, remove just this square's highlight
      const newHighlights = { ...highlightSquares };
      delete newHighlights[square];
      setHighlightSquares(newHighlights);
    } else {
      // If not highlighted or highlighted with a different color, add highlight with current color
      setHighlightSquares({ ...highlightSquares, [square]: { backgroundColor: activeColor } });
    }
  };

  const [moveFrom, setMoveFrom] = useState("");
  const [moveTo, setMoveTo] = useState(null);
  const [showPromotionDialog, setShowPromotionDialog] = useState(false);
  const [optionSquares, setOptionSquares] = useState({});

  // Function to safely mutate the game state
  const safeGameMutate = (modify) => {
    setGame(g => {
      const update = { ...g };
      modify(update);
      return update;
    });
  };

  // Function to get move options for a square
  const getMoveOptions = (square) => {
    if (!game.get(square)) return false;

    // Always highlight the selected square, regardless of the showLegalMoves setting
    const newSquares = {
      [square]: SELECTED_SQUARE_COLOR
    };

    // Only add the legal move circles when showLegalMoves is true
    if (showLegalMoves) {
      const moves = game.moves({
        square,
        verbose: true
      });

      if (moves.length > 0) {
        moves.forEach(move => {
          newSquares[move.to] = {
            background: game.get(move.to) && game.get(move.to).color !== game.get(square).color
              ? "radial-gradient(circle, rgba(0,0,0,.1) 85%, transparent 85%)"
              : "radial-gradient(circle, rgba(0,0,0,.1) 25%, transparent 25%)",
            borderRadius: "50%"
          };
        });
      }
    }

    setOptionSquares(newSquares);
    return true; // Always return true if a piece is at the square, indicating it was selected
  };

  // Function to handle square clicks
  const onSquareClick = (square) => {
    // If not viewing the latest position, return to it first
    if (currentMoveIndex < fenHistory.length - 1) {
      goToMoveIndex(fenHistory.length - 1);
      return;
    }

    // Clear all right-clicked highlights when a square is clicked
    setHighlightSquares({});

    // From square selection
    if (!moveFrom) {
      const hasMoveOptions = getMoveOptions(square);
      if (hasMoveOptions) setMoveFrom(square);
      return;
    }

    // To square selection
    if (!moveTo) {
      // Check if valid move before proceeding
      const moves = game.moves({
        verbose: true
      });
      const foundMove = moves.find(m => m.from === moveFrom && m.to === square);

      // Not a valid move
      if (!foundMove) {
        // Check if clicked on new piece
        const hasMoveOptions = getMoveOptions(square);
        // If new piece, setMoveFrom, otherwise clear moveFrom
        setMoveFrom(hasMoveOptions ? square : "");
        return;
      }

      // Valid move
      setMoveTo(square);

      // If promotion move
      if ((foundMove.color === "w" && foundMove.piece === "p" && square[1] === "8") ||
        (foundMove.color === "b" && foundMove.piece === "p" && square[1] === "1")) {
        setShowPromotionDialog(true);
        return;
      }

      // Normal move
      try {
        // Make the move
        const move = game.move({
          from: moveFrom,
          to: square,
          promotion: "q" // Default to queen promotion
        });

        // If the move is illegal, return
        if (!move) return;

        // Highlight the start and end squares of the move
        const highlightSquares = {
          [moveFrom]: MOVE_HIGHLIGHT_COLOR,
          [square]: MOVE_HIGHLIGHT_COLOR
        };
        setMoveSquares(highlightSquares);

        // Also update lastMoveSquares for the chessboard component
        setLastMoveSquares({
          from: moveFrom,
          to: square
        });

        setSanHistory((prev) => [...prev, move.san]);
        const newFen = game.fen();
        setFenHistory((prev) => [...prev, newFen]);
        setCurrentMoveIndex(fenHistory.length);
        setCurrentPlayer(game.turn() === 'w' ? 'white' : 'black');

        // Send the move to the server
        playMove(gameId, `${moveFrom}${square}`);

        if (game.isCheckmate()) {
          const winner = game.turn() === 'w' ? 'Black' : 'White';
          setGameResult(`${winner} Wins by Checkmate`);
          setShowPopup(true);
        } else if (game.isDraw()) {
          setGameResult('The Game Ends In A Draw');
          setShowPopup(true);
        }
      } catch (error) {
        console.error("Error making move:", error);
      }

      // Reset states after move
      setMoveFrom("");
      setMoveTo(null);
      setOptionSquares({});
      return;
    }
  };

  // Function to handle promotion piece selection
  const onPromotionPieceSelect = (piece) => {
    // If no piece passed then user has cancelled dialog, don't make move and reset
    if (piece) {
      try {
        // Make the move with the selected promotion piece
        const move = game.move({
          from: moveFrom,
          to: moveTo,
          promotion: piece[1].toLowerCase() ?? "q"
        });

        if (move) {
          // Highlight the start and end squares of the move
          const highlightSquares = {
            [moveFrom]: MOVE_HIGHLIGHT_COLOR,
            [moveTo]: MOVE_HIGHLIGHT_COLOR
          };
          setMoveSquares(highlightSquares);

          // Also update lastMoveSquares for the chessboard component
          setLastMoveSquares({
            from: moveFrom,
            to: moveTo
          });

          setSanHistory((prev) => [...prev, move.san]);
          const newFen = game.fen();
          setFenHistory((prev) => [...prev, newFen]);
          setCurrentMoveIndex(fenHistory.length);
          setCurrentPlayer(game.turn() === 'w' ? 'white' : 'black');

          // Send the move to the server
          playMove(gameId, `${moveFrom}${moveTo}${piece[1].toLowerCase()}`);

          if (game.isCheckmate()) {
            const winner = game.turn() === 'w' ? 'Black' : 'White';
            setGameResult(`${winner} Wins by Checkmate`);
            setShowPopup(true);
          } else if (game.isDraw()) {
            setGameResult('The Game Ends In A Draw');
            setShowPopup(true);
          }
        }
      } catch (error) {
        console.error("Error making promotion move:", error);
      }
    }

    // Reset states
    setMoveFrom("");
    setMoveTo(null);
    setShowPromotionDialog(false);
    setOptionSquares({});
  };

  // Update the existing handleSquareLeftClick function to work with our new click-to-move system
  const handleSquareLeftClick = (square) => {
    // If there are any right-click highlights, clear them
    if (Object.keys(highlightSquares).length > 0) {
      setHighlightSquares({});
    }

    // Then handle the normal click-to-move logic
    onSquareClick(square);
  };

  const [showPopup, setShowPopup] = useState(false);
  const [time, setTime] = useState({ white: 60, black: 60 });
  const [currentPlayer, setCurrentPlayer] = useState('white');
  const [gameResult, setGameResult] = useState(null);

  // Collapse toggles - initialize from localStorage if available
  const [isCollapsed, setIsCollapsed] = useState(
    localStorage.getItem('chessMenuCollapsed') !== null
      ? localStorage.getItem('chessMenuCollapsed') === 'true'
      : true
  );
  const [isChatCollapsed, setIsChatCollapsed] = useState(
    localStorage.getItem('chessChatCollapsed') !== null
      ? localStorage.getItem('chessChatCollapsed') === 'true'
      : true
  );

  // Save collapse states to localStorage when they change
  useEffect(() => {
    localStorage.setItem('chessMenuCollapsed', isCollapsed);
  }, [isCollapsed]);

  useEffect(() => {
    localStorage.setItem('chessChatCollapsed', isChatCollapsed);
  }, [isChatCollapsed]);

  const currentMoveRef = useRef(null);
  const chatEndRef = useRef(null);

  // Update window height when resized
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for changes to showLegalMoves in localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'showLegalMoves') {
        setShowLegalMoves(e.newValue !== 'false');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Automatically adjust board size when window height changes
  useEffect(() => {
    const maxSize = calculateMaxBoardSize();
    // Allow a small buffer (20px) before resizing to provide more leeway
    // Only resize if current board size significantly exceeds available space
    if (boardSize > maxSize + 20) {
      setBoardSize(maxSize);
      localStorage.setItem('boardSize', maxSize);
    }
  }, [windowHeight, boardSize]);

  // Calculate the responsive heights for containers
  const getMoveHistoryHeight = () => {
    // Base height for smaller screens (13" MacBook)
    const baseHeight = 250;
    // For larger screens, grow proportionally
    const minHeight = 768; // Height of a 13" MacBook screen approximately

    if (windowHeight <= minHeight) return baseHeight;

    // Calculate how much extra height we have
    const extraHeight = windowHeight - minHeight;
    // Move history gets 70% of extra space
    return baseHeight + (extraHeight * 0.4);
  };

  const getChatHeight = () => {
    // Base height for smaller screens
    const baseHeight = 100;
    const minHeight = 768;

    if (windowHeight <= minHeight) return baseHeight;

    // Calculate extra height
    const extraHeight = windowHeight - minHeight;
    // Chat gets 30% of extra space
    return baseHeight + (extraHeight * 0.2);
  };

  const hideColumn = () => {
    setIsCollapsed(true);
    localStorage.setItem('chessMenuCollapsed', 'true');
  };

  const expandColumn = () => {
    setIsCollapsed(false);
    localStorage.setItem('chessMenuCollapsed', 'false');
  };

  // Scroll active move into view
  const scrollToCurrentMove = () => {
    setTimeout(() => {
      // Special case for the starting position
      if (currentMoveIndex === 0) {
        const scrollContainer = document.getElementById('moveHistoryContainer');
        if (scrollContainer) {
          scrollContainer.scrollTop = 0; // Scroll to the top
        }
        return;
      }

      // Normal case - scroll to the current move
      if (currentMoveRef.current) {
        currentMoveRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }, 100);
  };

  useEffect(() => {
    if (gameId) {
      props.connectToGameStream(gameId);
    }
  }, [gameId]);

  // Arrow key listener - modified to handle Up/Down and ensure current move is visible
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (currentMoveIndex > 0) {
          goToMoveIndex(currentMoveIndex - 1);
        }
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (currentMoveIndex < fenHistory.length - 1) {
          goToMoveIndex(currentMoveIndex + 1);
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        goToMoveIndex(0); // Go to first move
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        goToMoveIndex(fenHistory.length - 1); // Go to last move
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fenHistory, currentMoveIndex]);


  // Add effect to scroll current move into view when it changes
  useEffect(() => {
    scrollToCurrentMove();
  }, [currentMoveIndex]);

  useEffect(() => {
    const lichesstoken = localStorage.getItem('LichessIdToken');
    const controller = new AbortController();
    if (lichesstoken && props.connectToEventStream) {
      props.connectToEventStream(controller);
    }

    return () => controller.abort();
  }, []);


  // Format mm:ss
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Format material advantage display
  const formatAdvantage = (advantage) => {
    if (advantage === 0) return "";
    return advantage > 0 ? `+${advantage}` : `${advantage}`;
  };

  // Add timer effect to count down when it's a player's turn
  useEffect(() => {
    // Only run the timer if:
    // 1. Both players have made their first moves
    // 2. Timer is active
    // 3. Viewing latest position
    // 4. Game is not over
    let interval;
    const numMoves = game.history().length;
    if (numMoves >= 2 && showPopup === false) {
      interval = setInterval(() => {
        setTime(prevTime => {
          // Copy the previous time object
          const newTime = { ...prevTime };

          // Decrement the current player's time
          if (newTime[currentPlayer] > 0) {
            newTime[currentPlayer] -= 1;

            // Handle time running out
            if (newTime[currentPlayer] === 0) {
              const winner = currentPlayer === 'white' ? 'Black' : 'White';
              setGameResult(`${winner} Wins by Timeout`);
              setShowPopup(true);
              clearInterval(interval);
            }
          }

          return newTime;
        });
      }, 1000);
    }

    // Clean up the timer when the component unmounts or dependencies change
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentPlayer, currentMoveIndex, fenHistory.length, gameResult]);

  // Function to update move squares based on current position index
  const updateMoveSquaresForPosition = (index) => {
    if (index === 0) {
      // Starting position has no moves to highlight
      setMoveSquares({});
      setLastMoveSquares(null);
      return;
    }

    // Get the move that led to this position
    const move = sanHistory[index - 1];
    if (!move) {
      setMoveSquares({});
      setLastMoveSquares(null);
      return;
    }

    // Create a temporary chess instance to get the move details
    const tempGame = new Chess(fenHistory[index - 1]);
    const moveDetails = tempGame.moves({ verbose: true })
      .find(m => m.san === move);

    if (moveDetails) {
      // Create an object with the start and end squares highlighted
      const highlightSquares = {
        [moveDetails.from]: MOVE_HIGHLIGHT_COLOR,
        [moveDetails.to]: MOVE_HIGHLIGHT_COLOR
      };
      setMoveSquares(highlightSquares);

      // Also update lastMoveSquares for the chessboard component
      setLastMoveSquares({
        from: moveDetails.from,
        to: moveDetails.to
      });
    } else {
      // If moveDetails couldn't be found (shouldn't happen in normal cases)
      console.warn(`Could not find move details for move: ${move}`);
      setMoveSquares({});
      setLastMoveSquares(null);
    }
  };

  // Update move squares whenever current position changes
  useEffect(() => {
    updateMoveSquaresForPosition(currentMoveIndex);
  }, [currentMoveIndex]);

  // Clear move squares when game resets
  useEffect(() => {
    if (fenHistory.length === 1) {
      setMoveSquares({});
      setLastMoveSquares(null);
    }
  }, [fenHistory]);

  const capitalized = (word) => word.charAt(0).toUpperCase() + word.slice(1);

  // Handling all game state received on stream
  useEffect(() => {
    if (!props?.game?.currentGameState?.state) {
      return
    };
    // const latestMove = props.game.currentGameState.moves.split(" ").at(-1);
    const state = props.game.currentGameState.state;
    const game = props.game.games?.[0];
    const color = game.color;
    if (state.winner) {
      const winner = capitalized(state.winner);
      const isResignation = state.status === "resign";
      if (isResignation) {
        setGameResult(`${winner} Wins by Resignation`);
      } else {
        setGameResult(`${winner} Wins`);
      }
      setShowPopup(true);
    }

    if (state.status === "draw") {
      setGameResult('The Game Ends In A Draw');
      setShowPopup(true);
    }

    if (
      (color === 'white' && state.bdraw) ||
      (color === 'black' && state.wdraw)
    ) {
      alert("Draw Offer received");
    }
    const moves = state.moves.split(" ");
    const newGame = new Chess();
    const newFenHistory = [newGame.fen()];
    const newSanHistory = [];

    let previousFen = newGame.fen();

    for (let move of moves) {
      if (!move) {
        continue;
      }

      // Track SAN moves for highlighting
      const fromSquare = move.slice(0, 2);
      const toSquare = move.slice(2, 4);

      // Make the move and save its SAN representation
      const chessMove = newGame.move({ from: fromSquare, to: toSquare });
      if (chessMove) {
        newSanHistory.push(chessMove.san);
      }

      newFenHistory.push(newGame.fen());
      previousFen = newGame.fen();
    }

    setGame(newGame);
    setFenHistory(newFenHistory);
    setSanHistory(newSanHistory);
    setCurrentMoveIndex(newFenHistory.length - 1);

    // Set move highlighting for the latest move
    if (moves.length > 0) {
      const lastMove = moves[moves.length - 1];
      if (lastMove && lastMove.length >= 4) {
        const fromSquare = lastMove.slice(0, 2);
        const toSquare = lastMove.slice(2, 4);

        // Highlight the squares
        setMoveSquares({
          [fromSquare]: MOVE_HIGHLIGHT_COLOR,
          [toSquare]: MOVE_HIGHLIGHT_COLOR
        });

        // Also set lastMoveSquares for consistent highlighting
        setLastMoveSquares({
          from: fromSquare,
          to: toSquare
        });
      }
    }

    setOrientation(color);

    setCurrentPlayer(newGame.turn() === 'w' ? 'white' : 'black');
    setTime({ white: Math.floor(state.wtime / 1000), black: Math.floor(state.btime / 1000) });
  }, [props.game.currentGameState]);

  const chessboardRef = useRef(null); // Add ref for chessboard to access clearPremoves

  // Clear premoves when the game state changes
  const resetPremoves = () => {
    if (chessboardRef.current?.clearPremoves) {
      chessboardRef.current.clearPremoves();
    }
  };

  // Add effect to clear premoves when the game state changes significantly
  useEffect(() => {
    resetPremoves();
  }, [gameId]);

  // Function to find the positions of both kings on the board
  const findKingPositions = (chess) => {
    const board = chess.board();
    const positions = { white: null, black: null };

    // Correct method to find the kings
    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file];
        if (piece && piece.type === 'k') {
          const square = String.fromCharCode(97 + file) + (8 - rank);
          positions[piece.color === 'w' ? 'white' : 'black'] = square;
        }
      }
    }

    return positions;
  };

  // Helper function to convert chess notation to coordinates
  const squareToCoordinates = (square) => {
    const file = square.charCodeAt(0) - 'a'.charCodeAt(0);
    const rank = 8 - parseInt(square[1]);
    return { file, rank };
  };

  // Function to render the status icons overlay
  const renderStatusIconsOverlay = () => {
    const overlayElements = [];
    const squareSize = boardSize / 8;

    if (kingPositions.white && kingPositions.black) {
      // Handle checkmate scenario
      if (gameState.isCheckmate && gameState.winner) {
        // Find winning king position
        const winningKingSquare = gameState.winner === 'white' ? kingPositions.white : kingPositions.black;
        const losingKingSquare = gameState.winner === 'white' ? kingPositions.black : kingPositions.white;

        if (winningKingSquare) {
          const { file: winFile, rank: winRank } = squareToCoordinates(winningKingSquare);
          overlayElements.push(
            <img
              key={`winner-king`}
              src={winnerIcon}
              alt="Winning king"
              style={{
                position: 'absolute',
                top: `${winRank * squareSize - 4}px`, // Adjusted to prevent top cut-off
                left: `${winFile * squareSize + (squareSize - 15)}px`,
                width: '24px',
                height: '24px',
                pointerEvents: 'none',
                zIndex: 1000 // Higher z-index to ensure it's above everything
              }}
            />
          );
        }

        if (losingKingSquare) {
          const { file: loseFile, rank: loseRank } = squareToCoordinates(losingKingSquare);
          overlayElements.push(
            <img
              key={`loser-king`}
              src={loserIcon}
              alt="Losing king"
              style={{
                position: 'absolute',
                top: `${loseRank * squareSize - 4}px`, // Adjusted to prevent top cut-off
                left: `${loseFile * squareSize + (squareSize - 15)}px`,
                width: '24px',
                height: '24px',
                pointerEvents: 'none',
                zIndex: 1000
              }}
            />
          );
        }
      }
      // Handle check scenario (but not checkmate)
      else if (gameState.isCheck) {
        // The king in check is the one whose turn it is in the current position
        const kingInCheckColor = game.turn() === 'w' ? 'white' : 'black';
        const kingInCheckSquare = kingPositions[kingInCheckColor];

        if (kingInCheckSquare) {
          const { file, rank } = squareToCoordinates(kingInCheckSquare);
          overlayElements.push(
            <img
              key={`check-king`}
              src={checkIcon}
              alt="King in check"
              style={{
                position: 'absolute',
                top: `${rank * squareSize - 4}px`, // Adjusted to prevent top cut-off
                left: `${file * squareSize + (squareSize + 25)}px`,
                width: '24px',
                height: '24px',
                pointerEvents: 'none',
                zIndex: 1000
              }}
            />
          );
        }
      }
      // Handle draw scenario
      else if (gameState.isDraw) {
        // Add draw icon to both kings
        [kingPositions.white, kingPositions.black].forEach((kingSquare, index) => {
          if (kingSquare) {
            const { file, rank } = squareToCoordinates(kingSquare);
            overlayElements.push(
              <img
                key={`draw-king-${index}`}
                src={drawIcon}
                alt="Draw"
                style={{
                  position: 'absolute',
                  top: `${rank * squareSize - 12}px`, // Adjusted to prevent top cut-off
                  left: `${file * squareSize + (squareSize - 15)}px`,
                  width: '24px',
                  height: '24px',
                  pointerEvents: 'none',
                  zIndex: 1000
                }}
              />
            );
          }
        });
      }
    }

    return overlayElements;
  };

  // Modified onDrop function to handle premoves
  const onDrop = (sourceSquare, targetSquare, piece) => {
    if (currentMoveIndex < fenHistory.length - 1) {
      alert('Return to the latest position first.');
      return false;
    }

    try {
      // Try to make the move
      const move = game.move({
        from: sourceSquare,
        to: targetSquare,
        promotion: 'q' // Default to queen promotion
      });

      // If the move is illegal, return false (but premoves will still be registered if enabled)
      if (!move) return false;

      // Clear all right-clicked highlights when a move is made
      setHighlightSquares({});
      setOptionSquares({});

      // Highlight the start and end squares of the move
      const highlightSquares = {
        [sourceSquare]: MOVE_HIGHLIGHT_COLOR,
        [targetSquare]: MOVE_HIGHLIGHT_COLOR
      };
      setMoveSquares(highlightSquares);

      // Also update lastMoveSquares for the chessboard component to consistently show the move
      setLastMoveSquares({
        from: sourceSquare,
        to: targetSquare
      });

      setSanHistory((prev) => [...prev, move.san]);
      const newFen = game.fen();
      setFenHistory((prev) => [...prev, newFen]);
      setCurrentMoveIndex(fenHistory.length);
      setCurrentPlayer(game.turn() === 'w' ? 'white' : 'black');

      playMove(gameId, `${sourceSquare}${targetSquare}`);

      // Update game state for check/checkmate/draw and king positions
      updateGameState(game);

      if (game.isCheckmate()) {
        const winner = game.turn() === 'w' ? 'Black' : 'White';
        setGameResult(`${winner} Wins by Checkmate`);
        setShowPopup(true);
      } else if (game.isDraw()) {
        setGameResult('The Game Ends In A Draw');
        setShowPopup(true);
      }
      return true;
    } catch {
      return false;
    }
  };

  const goToMoveIndex = (index) => {
    if (index < 0 || index >= fenHistory.length) return;

    setCurrentMoveIndex(index);
    const newGame = new Chess(fenHistory[index]);
    setGame(newGame);
    setCurrentPlayer(newGame.turn() === 'w' ? 'white' : 'black');

    // Update material advantage and piece differential
    setMaterialAdvantage(getMaterialAdvantage(newGame));
    setPieceDifferential(getPieceDifferential(newGame));

    // Update move squares for this position
    updateMoveSquaresForPosition(index);

    // Update game state for check/checkmate/draw and king positions
    updateGameState(newGame);

    // Use a longer delay if coming from index 0 to the last move
    const delay = (index === 0 && index === fenHistory.length - 1) ? 100 : 50;

    // Add a short delay before scrolling to ensure state updates have propagated
    setTimeout(() => {
      // Force the currentMoveRef to update based on the new index
      if (index === fenHistory.length - 1 && index === 0) {
        const scrollContainer = document.getElementById('moveHistoryContainer');
        if (scrollContainer && movePairs.length > 0) {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }
      } else {
        scrollToCurrentMove();
      }
    }, delay);
  };

  const handleChatSubmit = (e) => {
    e.preventDefault();
    if (!currentMessage.trim()) return;
    setChatMessages([...chatMessages, { text: currentMessage, sender: 'You' }]);
    setCurrentMessage('');
  };

  // Update existing newGame function to clear premoves
  const newGame = async () => {
    const currentGame = props?.game?.games?.[0];
    const currentGameState = props?.game?.currentGameState;
    if (!currentGame) {
      alert('No game found');
      return;
    }

    const opponent = currentGame?.opponent;
    if (!opponent) {
      alert('No opponent found');
      return;
    }
    if (opponent?.ai) {
      const level = opponent?.ai;
      const clock = currentGameState?.clock;
      const response = await startGameWithAI(level, clock.initial / 1000, clock.increment / 1000);
    } else {
      const username = opponent.username;
      const clock = currentGameState?.clock;
      const response = await challengeAPlayer(username, clock.initial / 1000, clock.increment / 1000);
    }
    props.history.push('/playchess');
  };

  // Toggle through board sizes
  // We will need to save the most recent size to database storage, so it is the same as their last game
  const toggleBoardSize = () => {
    // Debug log
    const sizes = [200, 300, 400, 500, 600, 700, 800, 900, 1000, 1100, 1200];
    const currentIndex = sizes.indexOf(boardSize);
    let nextSize;
    if (currentIndex === sizes.length - 1 || boardSize >= calculateMaxBoardSize()) {
      nextSize = sizes[0];
    } else {
      nextSize = sizes[currentIndex + 1];
    }

    if (nextSize > calculateMaxBoardSize()) {
      nextSize = calculateMaxBoardSize();
    }

    if (boardSize !== nextSize) {
      // Debug log
      setBoardSize(nextSize);
      localStorage.setItem('boardSize', nextSize);
    }
  };

  // Convert SAN to PGN
  const buildFullPGN = () => {
    const tempGame = new Chess();
    for (let move of sanHistory) {
      tempGame.move(move);
    }
    return tempGame.pgn();
  };

  const savePGN = () => {
    const pgn = buildFullPGN();
    // Store PGN in localStorage to pass to Analysis tab
    localStorage.setItem('analysisImportPGN', pgn);

    // Open in a new tab instead of navigating in the current window
    window.open('/Analysis', '_blank');

    setShowPopup(false);
  };

  const copyPGN = () => {
    const pgn = buildFullPGN();
    navigator.clipboard.writeText(pgn)
      .then(() => alert('PGN copied to clipboard!'))
      .catch(console.error);
  };

  const resign = async () => {
    if (!gameId) {
      alert('No game in progress');
      return;
    }

    try {
      await resignGame(gameId);
      // Set the result when resigning
      const winner = game.turn() === 'w' ? 'Black' : 'White';
      setGameResult(`${winner} Wins by Resignation`);
      setShowPopup(true);
    } catch (error) {
      console.error('Error resigning game:', error);
    }
  };
  const requestDraw = async () => {
    if (!gameId) {
      alert('No game in progress');
      return;
    }

    try {
      await offerDraw(gameId);
    } catch (error) {
      console.error('Error resigning game:', error);
    }
  };
  const takebackMove = () => alert('Takeback Move');

  // Build pairs in normal order, so first move is earliest.
  const movePairs = [];
  for (let i = 0; i < sanHistory.length; i += 2) {
    const moveNum = Math.floor(i / 2) + 1;
    const whiteMove = sanHistory[i] || '';
    const blackMove = sanHistory[i + 1] || '';
    movePairs.push({
      moveNum,
      whiteMove,
      blackMove,
      whiteIndex: i,
      blackIndex: i + 1,
    });
  }

  // Highlight if current.
  const isCurrentMove = (index) => {
    return currentMoveIndex - 1 === index;
  };

  // Calculate material difference for each side
  const whiteMaterialDisplay = materialAdvantage > 0 ? formatAdvantage(materialAdvantage) : "";
  const blackMaterialDisplay = materialAdvantage < 0 ? formatAdvantage(-materialAdvantage) : "";

  // Get piece icons for each side
  const whiteAdvantageIcons = renderPieceIcons(pieceDifferential, true);
  const blackAdvantageIcons = renderPieceIcons(pieceDifferential, false);

  // Whether to show the material advantage for each side
  const showWhiteAdvantage = whiteAdvantageIcons.length > 0;
  const showBlackAdvantage = blackAdvantageIcons.length > 0;

  // Calculate the move history and chat container heights
  const moveHistoryHeight = getMoveHistoryHeight();
  const chatHeight = getChatHeight();

  // Add an effect to scroll to the bottom when moves are added
  useEffect(() => {
    const scrollContainer = document.getElementById('moveHistoryContainer');
    if (scrollContainer) {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  }, [sanHistory]);

  // Function to scroll to the bottom of the chat
  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Scroll to bottom whenever chatMessages change
  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionToConfirm, setActionToConfirm] = useState('');

  const handleActionClick = (action) => {
    setActionToConfirm(action);
    setIsModalOpen(true);
  };

  const handleConfirm = async () => {
    // Perform the action based on actionToConfirm
    if (actionToConfirm === 'resign') {
      await resign();
    } else if (actionToConfirm === 'draw') {
      await requestDraw();
    } else if (actionToConfirm === 'takeback') {
      alert('Takeback request sent.');
    }
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const { renderUserWithBadge } = usePremium();

  // Function to update game state (check, checkmate, draw)
  const updateGameState = (chess) => {
    const isCheckmate = chess.isCheckmate();
    const isCheck = chess.isCheck();
    const isDraw = chess.isDraw() || chess.isStalemate() || chess.isThreefoldRepetition() || chess.isInsufficientMaterial();

    // Determine winner in case of checkmate
    let winner = null;
    if (isCheckmate) {
      // If it's checkmate, the winner is opposite of the current turn
      winner = chess.turn() === 'w' ? 'black' : 'white';
    }

    setGameState({ isCheckmate, isCheck, isDraw, winner });

    // Update king positions
    const positions = findKingPositions(chess);
    setKingPositions(positions);
  };

  // Update game state when making a move or loading a position
  useEffect(() => {
    if (game) {
      updateGameState(game);
    }
  }, [game, currentMoveIndex]);

  return (
    <div className='app-container'>
      <div className='explore-container main-column' style={{ display: 'flex', flexDirection: 'row', border: '' }}>
        <div
          style={{
            width: isCollapsed ? '100px' : '20%',
            borderTopLeftRadius: '5px',
            borderBottomLeftRadius: '5px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            overflowY: 'auto',
            flexShrink: 0,
            margin: '0px 10px',
          }}
        >
          {isCollapsed ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: '', justifyContent: 'space-around', gap: '', marginTop: '', height: '100%' }}>

              <div className="tooltip-container">
                <i className="fa-solid fa-arrows-left-right-to-line menu-icon" onClick={expandColumn}
                  style={{
                    width: '100px',
                    backgroundColor: 'transparent',
                    color: 'gray',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    height: '50px',
                    fontSize: '40px',
                    fontWeight: 'bold',
                    textAlign: 'center',
                  }}></i>
                <span className="tooltip">Expand Menu</span>
              </div>

              <div className="tooltip-container">
                <i className="fa-solid fa-retweet menu-icon" style={{
                  width: '100px',
                  backgroundColor: 'transparent',
                  color: 'gray',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  height: '50px',
                  fontSize: '40px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                }}
                  onClick={toggleBoardOrientation}></i>
                <span className="tooltip">Flip Board</span>
              </div>

              {!gameResult ? (
                // Game in progress - show regular action buttons
                <>
                  <div className="tooltip-container">
                    <i className="fa-solid fa-flag fa-flip-horizontal resign-button" onClick={() => handleActionClick('resign')}
                      style={{
                        width: '100px',
                        backgroundColor: 'transparent',
                        color: 'gray',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        height: '50px',
                        fontSize: '40px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                      }}></i>
                    <span className="tooltip">Resign Game</span>
                  </div>

                  <div className="tooltip-container">
                    <i className="fa-solid fa-handshake menu-icon" onClick={() => handleActionClick('draw')}
                      style={{
                        width: '100px',
                        backgroundColor: 'transparent',
                        color: 'gray',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        height: '50px',
                        fontSize: '40px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                      }}></i>
                    <span className="tooltip">Offer Draw</span>
                  </div>

                  {/*<div className="tooltip-container">
                  <i className="fa-solid fa-arrow-rotate-left" onClick={() => handleActionClick('takeback')}
                  style={{
                    width: '100px',
                    backgroundColor: 'transparent',
                    color: 'gray',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    height: '50px',
                    fontWeight: 'bold',
                    fontSize: '40px',
                    textAlign: 'center',
                  }}></i>
                  <span className="tooltip">Undo</span>
                </div>  */}
                </>
              ) : (
                // Game over - show post-game buttons
                <>
                  <button
                    onClick={newGame}
                    style={{
                      width: '100px',
                      backgroundColor: 'transparent',
                      color: 'gray',
                      border: '3px solid gray',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      height: '50px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    Rematch
                  </button>
                  <button
                    onClick={newGame}
                    style={{
                      width: '100px',
                      backgroundColor: 'transparent',
                      color: 'gray',
                      border: '3px solid gray',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      height: '50px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    New Game
                  </button>
                  <button
                    onClick={savePGN}
                    style={{
                      width: '100px',
                      backgroundColor: 'transparent',
                      color: 'gray',
                      border: '3px solid gray',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      height: '50px',
                      fontWeight: 'bold',
                      fontSize: '16px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    Analyze
                  </button>
                </>
              )}

              <div className="tooltip-container">
                <i className="fa-solid fa-chess-board menu-icon" onClick={toggleBoardSize} style={{
                  padding: '',
                  backgroundColor: 'transparent',
                  color: 'gray',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  width: '100%',
                  fontSize: '40px',
                  textAlign: 'center',
                }}></i>
                <span className="tooltip">Board Size</span>
              </div>
              <div style={{ cursor: 'pointer', textAlign: 'center', color: 'gray', borderTop: '2px solid gray', fontSize: '12px', fontWeight: 'bold', paddingTop: '50px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '15px' }}>

                <div style={{ cursor: 'pointer' }} onClick={() => window.open('https://lichess.org', '_blank')}>POWERED BY LICHESS.ORG</div> <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img src={LichessLogo} alt="Lichess Logo" style={{ width: 30, height: 30 }} onClick={() => window.open('https://lichess.org', '_blank')} /></div></div>



            </div>
          ) : (
            <>
              {/* Chat */}
              {/* <div style={{ width: '90%', flex: '1 1 auto', marginTop: '10px' }}>
              {!isChatCollapsed ? (
                <>
                  <div
                    style={{
                      border: '3px solid gray',
                      height: `${chatHeight}px`,
                      minHeight: '100px',
                      maxHeight: '25vh',
                      display: 'flex',
                      flexDirection: 'column',
                      color: 'white',
                    }}
                  >
                   
                    <div
                      style={{
                        textAlign: 'center',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: 'gray',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderBottom: '2px solid gray',
                        padding: '5px 0',
                      }}
                    >
                      <button 
                        onClick={() => setIsChatCollapsed(true)}
                        style={{
                          position: 'absolute',
                          left: '10px',
                          background: 'transparent',
                          border: 'none',
                          color: 'gray',
                          cursor: 'pointer',
                          fontSize: '16px',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <i className="fa-solid fa-xmark"></i>
                      </button>
                      Chat
                    </div>
                    
                  
                    <div
                      style={{
                        overflowY: 'auto',
                        padding: '10px',
                        flex: '1',
                      }}
                    >
                      {chatMessages.map((msg, index) => (
                        <div style={{ marginBottom: '5px' }} key={index}>
                          <strong>{msg.sender}:</strong> {msg.text}
                        </div>
                      ))}
                      <div ref={chatEndRef} />
                    </div>
                  </div>
                  <div style={{ height: '40px', display: 'flex', flexDirection: 'row', justifyContent: 'center', marginTop: '5px', marginBottom: '10px' }}>
                    <form onSubmit={handleChatSubmit} style={{ display: 'flex', width: '100%' }}>
                      <input
                        type="text"
                        value={currentMessage}
                        onChange={(e) => setCurrentMessage(e.target.value)}
                        style={{
                          width: '75%',
                          borderRadius: '3px',
                          border: '2px solid gray',
                          marginRight: '5px',
                          height: '34px',
                          paddingLeft: '15px',
                          fontSize: '16px',
                          backgroundColor: 'black',
                          color: 'white'
                          
                        }}
                        placeholder='Start typing...'
                      />
                      <button
                        type="submit"
                        style={{
                          width: '25%',
                          backgroundColor: 'transparent',
                          border: '2px solid gray',
                          color: 'gray',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          height: '40px',
                          fontSize: '16px',
                          fontWeight: 'bold',
                        }}
                      >
                        Send
                      </button>
                    </form>
                  </div>
                </>
              ) : (
                <button
                  onClick={() => setIsChatCollapsed(false)}
                  style={{
                    width: '100%',
                    padding: '10px 0',
                    backgroundColor: 'transparent',
                    color: 'gray',
                    border: '3px solid gray',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: '18px',
                    marginBottom: '10px',
                    fontWeight: 'bold',
                  }}
                >
                  Open Chat
                </button>
              )}
            </div> */}

              {/* Move History */}
              <div style={{ width: '90%', flex: 'auto', marginTop: '10px', color: 'gray' }}>
                <div
                  style={{
                    border: '2px solid gray',
                    position: 'relative',
                    height: `${moveHistoryHeight}px`,
                    minHeight: '200px',
                    maxHeight: '50vh',
                    marginBottom: '0',
                    display: 'flex',
                    flexDirection: 'column',
                    scrollbarWidth: 'thin',
                  }}
                >
                  {/* Top: # White Black labels (sticky) */}
                  <div
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      display: 'grid',
                      gridTemplateColumns: '0.4fr 1fr 1fr',
                      backgroundColor: 'black',
                      color: 'gray',
                      textAlign: 'left',
                      paddingLeft: '20px',
                      paddingTop: '5px',
                      paddingBottom: '5px',
                      height: '30px',
                      alignItems: 'center',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      borderBottom: '3px solid gray'
                    }}
                  >
                    <div>#</div>
                    <div>White</div>
                    <div>Black</div>
                  </div>

                  {/* Scrollable area for moves in chronological order */}
                  <div
                    id="moveHistoryContainer"
                    style={{
                      flex: '1',
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0',
                    }}
                  >
                    {movePairs.map((pair, idx) => {
                      // Determine if either white or black move in this pair is the current move
                      const isCurrentWhite = isCurrentMove(pair.whiteIndex);
                      const isCurrentBlack = isCurrentMove(pair.blackIndex);

                      return (
                        <div
                          key={idx}
                          ref={isCurrentWhite || isCurrentBlack ? currentMoveRef : null}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '0.4fr 1fr 1fr',
                            height: `${Math.floor(moveHistoryHeight * 0.1)}px`, // 10% of container height
                            minHeight: '30px',
                            alignItems: 'center',
                            padding: '0 5px',
                            borderBottom: '1px solid #333',
                            overflowX: 'hidden',
                          }}
                        >
                          <div style={{
                            color: pair.moveNum === movePairs.length ? 'green' : 'gray',
                            textAlign: 'left',
                            fontWeight: pair.moveNum === movePairs.length ? 'bold' : 'normal',
                            paddingLeft: '20px'
                          }}>
                            {pair.moveNum}
                          </div>
                          <div>
                            {pair.whiteMove && (
                              <button
                                onClick={() => goToMoveIndex(pair.whiteIndex + 1)}
                                style={{
                                  background: isCurrentMove(pair.whiteIndex) ? 'green' : 'transparent',
                                  border: 'none',
                                  color: isCurrentMove(pair.whiteIndex) ? 'gold' : (pair.moveNum === movePairs.length ? 'green' : 'gray'),
                                  cursor: 'pointer',
                                  fontSize: '18px',
                                  fontWeight: pair.moveNum === movePairs.length ? 'bold' : 'normal',
                                  padding: isCurrentMove(pair.whiteIndex) ? '2px 8px' : '2px 5px',
                                  borderRadius: '3px',
                                  width: isCurrentMove(pair.whiteIndex) ? 'auto' : '100%',
                                  textAlign: 'left',
                                  marginLeft: '10px',
                                }}
                              >
                                {pair.whiteMove}
                              </button>
                            )}
                          </div>
                          <div>
                            {pair.blackMove && (
                              <button
                                onClick={() => goToMoveIndex(pair.blackIndex + 1)}
                                style={{
                                  background: isCurrentMove(pair.blackIndex) ? 'green' : 'transparent',
                                  border: 'none',
                                  color: isCurrentMove(pair.blackIndex) ? 'gold' : (pair.moveNum === movePairs.length ? 'green' : 'gray'),
                                  cursor: 'pointer',
                                  fontSize: '18px',
                                  fontWeight: pair.moveNum === movePairs.length ? 'bold' : 'normal',
                                  padding: isCurrentMove(pair.blackIndex) ? '2px 8px' : '2px 5px',
                                  borderRadius: '3px',
                                  width: isCurrentMove(pair.blackIndex) ? 'auto' : '100%',
                                  textAlign: 'left',
                                  marginLeft: '10px',
                                }}
                              >
                                {pair.blackMove}
                              </button>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Simple Controls for Move History Navigation */}
                <div style={{ width: '', display: 'flex', justifyContent: 'center', border: '2px solid gray', borderTop: 'none', padding: '10px 0px' }}>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button
                      onClick={() => {
                        goToMoveIndex(0);
                      }}
                      style={{
                        background: 'transparent',
                        border: '2px solid gray',
                        color: 'gray',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        padding: '5px 12px',
                        fontSize: '16px'
                      }}
                    >
                      <i className="fa-solid fa-backward-fast"></i>
                    </button>
                    <button
                      onClick={() => currentMoveIndex > 0 && goToMoveIndex(currentMoveIndex - 1)}
                      style={{
                        background: 'transparent',
                        border: '2px solid gray',
                        color: 'gray',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        padding: '5px 12px',
                        fontSize: '16px'
                      }}
                    >
                      <i className="fa-solid fa-chevron-left"></i>
                    </button>
                    <button
                      onClick={() => currentMoveIndex < fenHistory.length - 1 && goToMoveIndex(currentMoveIndex + 1)}
                      style={{
                        background: 'transparent',
                        border: '2px solid gray',
                        color: 'gray',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        padding: '5px 12px',
                        fontSize: '16px'
                      }}
                    >
                      <i className="fa-solid fa-chevron-right"></i>
                    </button>
                    <button
                      onClick={() => {
                        goToMoveIndex(fenHistory.length - 1);
                      }}
                      style={{
                        background: 'transparent',
                        border: '2px solid gray',
                        color: 'gray',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        padding: '5px 12px',
                        fontSize: '16px'
                      }}
                    >
                      <i className="fa-solid fa-forward-fast"></i>
                    </button>
                  </div>
                </div>
              </div>



              {/* Action Buttons */}
              <div style={{
                marginTop: '10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '5px',
                width: '90%',
                marginBottom: '25px'
              }}>

                {!gameResult ? (
                  // Game in progress - show regular game action buttons
                  <>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'row',
                      width: '100%',
                      justifyContent: 'center',
                      marginBottom: '25px',
                      gap: '80px'
                    }}>

                      <div className="tooltip-container">
                        <i className="fa-solid fa-flag fa-flip-horizontal resign-button" onClick={() => handleActionClick('resign')}
                          style={{
                            backgroundColor: 'transparent',
                            color: 'gray',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            textAlign: 'center',
                            fontSize: '40px',
                          }}></i>
                        <span className="tooltip">Resign Game</span>
                      </div>

                      <div className="tooltip-container">
                        <i className="fa-solid fa-handshake menu-icon" onClick={() => handleActionClick('draw')}
                          style={{
                            backgroundColor: 'transparent',
                            color: 'gray',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            textAlign: 'center',
                            fontSize: '40px',
                          }}></i>
                        <span className="tooltip">Offer Draw</span>
                      </div>

                      {/*<div className="tooltip-container" style={{ flex: 1 }}>
                      <i className="fa-solid fa-arrow-rotate-left" onClick={() => handleActionClick('takeback')}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: 'transparent',
                        color: 'gray',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        textAlign: 'center',
                        fontSize: '40px',
                        width: '100%',
                        display: 'block'
                      }}></i>
                      <span className="tooltip">Undo</span>
                    </div>  */}

                    </div>
                  </>
                ) : (
                  // Game over - show post-game action buttons
                  <>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gridGap: '5px',
                      width: '100%',
                      marginBottom: '25px'
                    }}>
                      <button
                        onClick={newGame} // Same function as the Rematch button in popup
                        style={{
                          padding: '15px 5px',
                          backgroundColor: 'transparent',
                          color: 'gray',
                          border: '3px solid gray',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontWeight: 'bold'
                        }}
                      >
                        Rematch
                      </button>
                      <button
                        onClick={newGame} // Same function as the New Game button in popup
                        style={{
                          padding: '15px 5px',
                          backgroundColor: 'transparent',
                          color: 'gray',
                          border: '3px solid gray',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontWeight: 'bold'
                        }}
                      >
                        New Game
                      </button>
                      <button
                        onClick={savePGN} // Same function as the Analyze button in popup
                        style={{
                          padding: '15px 5px',
                          backgroundColor: 'transparent',
                          color: 'gray',
                          border: '3px solid gray',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          gridColumn: '1 / span 2' // Make it span both columns
                        }}
                      >
                        Analyze & Share
                      </button>

                    </div>

                  </>
                )}

                <div style={{ display: 'flex', gap: '10px', height: '40px', justifyContent: 'center', alignItems: 'center', paddingBottom: '20px' }}>


                  <div className="tooltip-container" style={{ flex: 1 }}>
                    <i className="fa-solid fa-arrows-left-right-to-line menu-icon" onClick={hideColumn}
                      style={{
                        padding: '',
                        backgroundColor: 'transparent',
                        color: 'gray',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '40px',
                        textAlign: 'center',
                        width: '100%',
                        display: 'block'
                      }}></i>
                    <span className="tooltip">Shrink Menu</span>
                  </div>

                  <div className="tooltip-container" style={{ flex: 1 }}>
                    <i className="fa-solid fa-retweet menu-icon" style={{
                      padding: '',
                      backgroundColor: 'transparent',
                      color: 'gray',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontSize: '40px',
                      textAlign: 'center',
                      width: '100%',
                      display: 'block'
                    }}
                      onClick={toggleBoardOrientation}></i>
                    <span className="tooltip">Flip Board</span>
                  </div>

                  <div className="tooltip-container" style={{ flex: 1 }}>
                    <i className="fa-solid fa-chess-board menu-icon" onClick={toggleBoardSize} style={{
                      padding: '',
                      backgroundColor: 'transparent',
                      color: 'gray',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontSize: '40px',
                      textAlign: 'center',
                      width: '100%',
                      display: 'block'
                    }}></i>
                    <span className="tooltip">Increase Board Size</span>
                  </div>

                </div>
                <div style={{ cursor: 'pointer', textAlign: 'center', color: 'gray', borderTop: '2px solid gray', fontSize: '16px', fontWeight: 'bold', paddingTop: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  <img src={LichessLogo} alt="Lichess Logo" style={{ width: 20, height: 20 }} onClick={() => window.open('https://lichess.org', '_blank')} />
                  <span style={{ cursor: 'pointer' }} onClick={() => window.open('https://lichess.org', '_blank')}>POWERED BY LICHESS.ORG</span></div>
              </div>
            </>
          )}
        </div>



        {/* RIGHT COLUMN - Board */}
        <div style={{
          position: 'relative',
          borderRadius: '5px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: '1 1 auto',
        }}>
          <div style={{ boxShadow: '0px 0px 5px 0px gold', border: '2px solid gray', borderRadius: '5px', overflow: 'hidden' }}>
            <Chessboard
              position={fenHistory[currentMoveIndex]}
              onPieceDrop={onDrop}
              boardWidth={boardSize}
              boardOrientation={orientation}

              // New props for click-to-move functionality
              arePiecesDraggable={true}  // Keep draggable functionality
              onSquareClick={handleSquareLeftClick}
              onSquareRightClick={handleSquareRightClick}
              onPromotionPieceSelect={onPromotionPieceSelect}
              promotionToSquare={moveTo}
              showPromotionDialog={showPromotionDialog}

              // Add custom style for the dragging source square
              customSquareStyles={{ ...moveSquares, ...highlightSquares, ...optionSquares }}
              customDarkSquareStyle={boardTheme === 'custom' && customColors
                ? { background: customColors.dark }
                : currentTheme.dark}
              customLightSquareStyle={boardTheme === 'custom' && customColors
                ? { background: customColors.light }
                : currentTheme.light}
              showBoardNotation={showCoordinates}

              // Add the custom dragging source square style
              draggedPieceSourceSquareStyle={DRAGGING_SOURCE_SQUARE_STYLE}

              lastMoveSquares={lastMoveSquares}
              previewEnabled={true}
              arePremovesAllowed={true}
              ref={chessboardRef}
            />
            {/* Render the status icons overlay */}
            {renderStatusIconsOverlay()}
          </div>

          {/* Popup Menu - Positioned relative to the chessboard */}
          {showPopup && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: boardSize * 0.9, // Make it 90% of the chessboard width
                maxWidth: '500px',
                backgroundColor: 'rgba(0,0,0,0.97)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000,
                borderRadius: '15px',
                border: '2px solid #FFD700',
                boxShadow: '0 0 30px rgba(255, 215, 0, 0.3)',
              }}
            >
              <div
                style={{
                  width: '100%',
                  padding: '30px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                }}
              >
                {gameResult && (
                  <div style={{
                    fontSize: '30px',
                    fontWeight: 'bold',
                    color: 'gold',
                    textAlign: 'center',
                    marginBottom: '10px',
                    textTransform: 'uppercase',
                    letterSpacing: '2px',
                  }}>
                    {gameResult}
                  </div>
                )}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gridGap: '15px',
                  width: '100%'
                }}>
                  <button
                    onClick={newGame}
                    style={{
                      padding: '15px',
                      backgroundColor: '#3066be',
                      color: 'white',
                      border: '2px solid gold',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '22px',
                      fontWeight: 'bold'
                    }}
                  >
                    Rematch
                  </button>
                  <button
                    onClick={newGame}
                    style={{
                      padding: '15px',
                      backgroundColor: '#58b047',
                      color: 'white',
                      border: '2px solid gold',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '22px',
                      fontWeight: 'bold'
                    }}
                  >
                    New Game
                  </button>
                  <button
                    onClick={savePGN}
                    style={{
                      padding: '15px',
                      backgroundColor: '#ff9800',
                      color: 'white',
                      border: '2px solid gold',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '22px',
                      fontWeight: 'bold',
                      gridColumn: '1 / span 2' // Make it span both columns
                    }}
                  >
                    Analyze & Share
                  </button>
                  <button
                    onClick={() => window.location.href = '/play'}
                    style={{
                      padding: '15px',
                      backgroundColor: '#2196F3',
                      color: 'white',
                      border: '2px solid gold',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '22px',
                      fontWeight: 'bold',
                      gridColumn: '1 / span 2'
                    }}
                  >
                    Return To Matchmaking Page
                  </button>
                  <button
                    onClick={() => setShowPopup(false)}
                    style={{
                      padding: '15px',
                      backgroundColor: '#f44336',
                      color: 'white',
                      border: '2px solid gold',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '22px',
                      fontWeight: 'bold',
                      gridColumn: '1 / span 2',
                      marginTop: '10px'
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {isModalOpen && (
            <ActionConfirmModal
              action={actionToConfirm}
              onConfirm={handleConfirm}
              onClose={handleCancel}
            />
          )}
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1vh',
          padding: '0px 20px',
          borderRadius: '5px',
          fontSize: 'clamp(14px, 2vw, 24px)',
          flexShrink: 0,
          width: '130px',
          height: '100%',
        }}>
          <button
            onClick={() => setIsOpponentHidden(!isOpponentHidden)}
            style={{
              padding: '0.5vh',
              backgroundColor: 'transparent',
              color: 'gray',
              border: '2px solid gray',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: 'clamp(10px, 1.5vw, 12px)',
              width: '100%',
              textAlign: 'center',
            }}
          >
            {isOpponentHidden ? 'SHOW OPPONENT' : 'HIDE OPPONENT'}
          </button>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1vh',
            width: '100%'
          }}>
            <div>
              <img
                src={ChesssLogo}
                alt="profile"
                style={{
                  height: 'clamp(50px, 10vh, 100px)',
                  width: 'clamp(50px, 10vh, 100px)',
                  borderRadius: '50%',
                  border: '3px solid white'
                }}
              />
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              color: 'gold',
              fontWeight: 'bold',
              fontSize: 'clamp(12px, 1.8vw, 18px)',
              visibility: isOpponentHidden ? 'hidden' : 'visible'
            }}>Rating</div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              color: 'gold',
              fontWeight: 'bold',
              fontSize: 'clamp(12px, 1.8vw, 18px)',
              visibility: isOpponentHidden ? 'hidden' : 'visible'
            }}>OPPONENT USERNAME</div>

            {/* Black player's material advantage */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 'clamp(20px, 4vh, 40px)',
              width: '100%',
              margin: '10px'
            }}>
              {showBlackAdvantage ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#333',
                  borderRadius: '4px',
                  padding: '0.5vh 1vh',
                  fontSize: '18px',
                  gap: '5px'
                }}>
                  <span style={{ color: 'white', fontWeight: 'bold' }}>{blackMaterialDisplay}</span>
                  {blackAdvantageIcons}
                </div>
              ) : null}
            </div>

            <div
              style={{
                fontWeight: 'bold',
                textAlign: 'center',
                padding: '1vh',
                width: '90%',
                border: currentPlayer === 'black' ? '3px solid red' : '2px solid gold',
                color: currentPlayer === 'black' ? '#ff0000' : 'gold',
                fontSize: 'clamp(16px, 2.5vw, 24px)',
              }}
            >
              {orientation === 'white' ? formatTime(time.black) : formatTime(time.white)}
            </div>
            <div
              style={{
                fontWeight: 'bold',
                textAlign: 'center',
                width: '90%',
                padding: '1vh',
                border: currentPlayer === 'white' ? '3px solid red' : '2px solid gold',
                color: currentPlayer === 'white' ? '#ff0000' : 'gold',
                fontSize: 'clamp(16px, 2.5vw, 24px)',
              }}
            >
              {orientation === 'white' ? formatTime(time.white) : formatTime(time.black)}
            </div>

            {/* White player's material advantage */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 'clamp(20px, 4vh, 40px)',
              width: '100%',
              margin: '10px'
            }}>
              {showWhiteAdvantage ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#333',
                  borderRadius: '4px',
                  padding: '0.5vh 1vh',
                  fontSize: '18px',
                  gap: '5px'
                }}>
                  <span style={{ color: 'white', fontWeight: 'bold' }}>{whiteMaterialDisplay}</span>
                  {whiteAdvantageIcons}
                </div>
              ) : null}
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              color: 'gold',
              fontWeight: 'bold',
              fontSize: 'clamp(12px, 1.8vw, 18px)'
            }}>Rating</div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              color: 'gold',
              fontWeight: 'bold',
              fontSize: 'clamp(12px, 1.8vw, 18px)'
            }}>{renderUserWithBadge(`${props.user.credentials.handle}`)}</div>
            <div>
              <img
                src={ChesssLogo}
                alt="profile"
                style={{
                  height: 'clamp(50px, 10vh, 100px)',
                  width: 'clamp(50px, 10vh, 100px)',
                  borderRadius: '50%',
                  border: '3px solid white'
                }}
              />
            </div>
          </div>

          <button
            onClick={() => setShowPopup(true)}
            style={{
              padding: '0.5vh',
              backgroundColor: 'transparent',
              color: 'gray',
              border: '2px solid gray',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: 'clamp(10px, 1.5vw, 12px)',
              width: '100%',
              textAlign: 'center',
            }}
          >
            X
          </button>
        </div>
      </div>
    </div>


  );
};

const mapStateToProps = (state) => ({
  user: state.user,
  game: state.game,
});

export default connect(mapStateToProps, { connectToEventStream, connectToGameStream })(ChessGame);
